import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { AuthBackground } from "@/components/auth/AuthBackground";
import { AuthSidePanel } from "@/components/auth/AuthSidePanel";
import { pickRandomVariant, signInVariants } from "@/components/auth/authVariants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import HelpGenieLogo from "@/components/ui/HelpGenieLogo";
import { DemoGenieCloneService } from "@/services/demo/DemoGenieCloneService";
import { Roles } from "@/types/auth/auth";

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, user, userProfile, loading: authLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Check for pending demo clone
  const hasPendingClone = DemoGenieCloneService.hasPendingClone();

  // Pick a random marketing variant on mount — stays stable through re-renders
  const variant = useMemo(() => pickRandomVariant(signInVariants), []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const { error } = await signIn(email, password);

      if (error) {
        // Handle specific Supabase auth errors
        if (error.message === "Email not confirmed") {
          toast.error("Please verify your email address before signing in. Check your inbox for the verification link.");
        } else if (error.message === "Invalid login credentials") {
          toast.error("Invalid email or password");
        } else {
          toast.error(error.message || "An error occurred during sign in");
        }
        setLoading(false);
        return;
      }

      // Redirect to creating-genie page if there's a pending clone
      if (hasPendingClone) {
        navigate("/creating-genie", { replace: true });
      }
    } catch (err) {
      toast.error("An unexpected error occurred. Please try again.");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;

    if (user && userProfile) {
      // Redirect to creating-genie page if there's a pending clone/setup
      if (hasPendingClone) {
        navigate("/creating-genie", { replace: true });
        return;
      }

      const from = (location.state as { from?: Location })?.from;
      let targetPath = from?.pathname ?? "/dashboard";

      if (userProfile.role === Roles.Consumer) {
        targetPath = "/consumer";
      }

      navigate(targetPath, { replace: true });
    }
    // Remove the null check - let AuthProvider handle profile creation/loading
    // The navigation will happen when userProfile is set
  }, [user, userProfile, authLoading, navigate, hasPendingClone]);

  return (
    <div className="min-h-screen flex relative">
      {/* Logo - Top Left */}
      <a
        href="/"
        className="absolute top-8 left-8 z-20 inline-flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
      >
        <HelpGenieLogo size={32} />
        <div className="flex flex-col hidden sm:flex">
          <span className="font-bold text-lg text-white tracking-tight">Help Genie</span>
          <span className="text-xs text-gray-300 -mt-1">Now We're Talking</span>
        </div>
      </a>

      {/* Left side - Hero image background */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center overflow-hidden">
        <AuthBackground />
        <AuthSidePanel variant={variant} />
      </div>

      {/* Right side - Sign in form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div>
            <img src="/helpgenie.png" alt="Help Genie" className="h-12 w-auto mb-6" />
            <h2 className="text-3xl font-bold text-[#222B45]">Sign In</h2>
            <p className="mt-2 text-[#6B7280]">{hasPendingClone ? "Sign in to get your genie" : "Access your Help Genie dashboard"}</p>
            {hasPendingClone && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-green-800 font-medium">{"Your genie is ready to be created!"}</span>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end text-sm mt-2">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="font-medium text-genie-primary hover:text-[#3579cc]"
                >
                  Forgot Password?
                </button>
              </div>
            </div>

            {error && <div className="text-sm text-red-500 text-center">{error}</div>}

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold shadow-lg hover:from-purple-700 hover:to-blue-700 transition-all hover:-translate-y-0.5"
            >
              {loading ? "Signing In..." : "Sign In"}
            </Button>

            <div className="text-center text-sm">
              <span className="text-[#6B7280]">Don't have an account? </span>
              <button type="button" onClick={() => navigate("/signup")} className="text-genie-primary hover:text-[#3579cc] font-medium">
                Sign up
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
